import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import BulkImportModal from '../components/BulkImportModal';
import { Users, UserPlus, Shield, Building2, Key, Trash2, Edit3, CheckCircle, AlertCircle, RefreshCw, Lock, X, Eye, EyeOff, FileSpreadsheet } from 'lucide-react';

export default function UserManagement() {
    const { user: currentUser } = useAuth();
    const [users, setUsers] = useState([]);
    const [companies, setCompanies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pageError, setPageError] = useState('');
    const [successMsg, setSuccessMsg] = useState('');

    const [showModal, setShowModal] = useState(false);
    const [showImportModal, setShowImportModal] = useState(false);
    const [editId, setEditId] = useState(null);
    const [modalError, setModalError] = useState('');
    const [fieldErrors, setFieldErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [showPassword, setShowPassword] = useState(false);

    const [formData, setFormData] = useState({
        name: '',
        email: '',
        mobile_number: '',
        role: 'coordinator',
        company_id: '',
        password: '',
    });

    const fetchUsers = async () => {
        setLoading(true);
        setPageError('');
        try {
            const [uRes, cRes] = await Promise.all([
                axios.get('/api/users'),
                axios.get('/api/companies')
            ]);
            if (uRes.data.success) setUsers(uRes.data.users);
            if (cRes.data.success) setCompanies(cRes.data.companies);
        } catch (err) {
            setPageError(err.response?.data?.message || 'Failed to load users list.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchUsers();
    }, []);

    const openAddModal = () => {
        setEditId(null);
        setFormData({
            name: '',
            email: '',
            mobile_number: '',
            role: 'coordinator',
            company_id: '',
            password: '',
        });
        setModalError('');
        setFieldErrors({});
        setShowModal(true);
    };

    const openEditModal = (u) => {
        setEditId(u.id);
        setFormData({
            name: u.name,
            email: u.email,
            mobile_number: u.mobile_number,
            role: u.role,
            company_id: u.company_id || '',
            password: '',
        });
        setModalError('');
        setFieldErrors({});
        setShowModal(true);
    };

    const handleInputChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        if (fieldErrors[field]) {
            setFieldErrors(prev => ({ ...prev, [field]: null }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setModalError('');
        setFieldErrors({});
        setSubmitting(true);

        try {
            if (editId) {
                const res = await axios.put(`/api/users/${editId}`, formData);
                if (res.data.success) {
                    setSuccessMsg('User updated successfully!');
                    setShowModal(false);
                    fetchUsers();
                }
            } else {
                const res = await axios.post('/api/users', formData);
                if (res.data.success) {
                    setSuccessMsg(res.data.message || 'User created successfully!');
                    setShowModal(false);
                    fetchUsers();
                }
            }
        } catch (err) {
            if (err.response?.status === 422 && err.response?.data?.errors) {
                setFieldErrors(err.response.data.errors);
                setModalError(err.response.data.message || 'Validation error. Please check the fields below.');
            } else {
                setModalError(err.response?.data?.message || 'Operation failed. Please check inputs and try again.');
            }
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this user?')) return;
        try {
            const res = await axios.delete(`/api/users/${id}`);
            if (res.data.success) {
                setSuccessMsg('User deleted successfully.');
                fetchUsers();
            }
        } catch (err) {
            setPageError(err.response?.data?.message || 'Delete failed.');
        }
    };

    const renderFieldError = (field) => {
        if (!fieldErrors[field]) return null;
        const msg = Array.isArray(fieldErrors[field]) ? fieldErrors[field][0] : fieldErrors[field];
        return (
            <p className="text-red-400 text-xs font-semibold mt-1 flex items-center space-x-1">
                <span>⚠️ {msg}</span>
            </p>
        );
    };

    return (
        <div className="max-w-7xl mx-auto px-4 py-8 space-y-6">
            
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <span className="text-xs font-bold uppercase tracking-wider text-cyan-400 bg-cyan-500/10 px-3 py-1 rounded-full border border-cyan-500/20">
                        Access Control (Admin Only)
                    </span>
                    <h1 className="text-3xl font-extrabold mt-2 bg-gradient-to-r from-white via-slate-100 to-cyan-300 bg-clip-text text-transparent">
                        User Management
                    </h1>
                    <p className="text-sm text-slate-400">
                        Manage Admins, Coordinators, and Company HR team accounts.
                    </p>
                </div>

                <button
                    onClick={openAddModal}
                    className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white font-bold text-sm shadow-lg shadow-cyan-600/30 flex items-center space-x-2 transition-all"
                >
                    <UserPlus className="w-4 h-4" />
                    <span>Create New User</span>
                </button>
            </div>

            {/* Notifications on List Page */}
            {successMsg && (
                <div className="p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-300 text-sm flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                        <CheckCircle className="w-5 h-5 text-emerald-400 shrink-0" />
                        <span>{successMsg}</span>
                    </div>
                    <button onClick={() => setSuccessMsg('')} className="text-xs text-slate-400 hover:text-white font-bold">Dismiss</button>
                </div>
            )}

            {pageError && (
                <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/20 text-red-300 text-sm flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                        <AlertCircle className="w-5 h-5 text-red-400 shrink-0" />
                        <span>{pageError}</span>
                    </div>
                    <button onClick={() => setPageError('')} className="text-xs text-slate-400 hover:text-white font-bold">Dismiss</button>
                </div>
            )}

            {/* Users Table */}
            <div className="glass-card rounded-2xl overflow-hidden border border-slate-800 shadow-xl">
                <div className="overflow-x-auto">
                    <table className="w-full text-left text-sm text-slate-300">
                        <thead className="bg-slate-900/90 text-xs font-bold text-slate-400 uppercase tracking-wider border-b border-slate-800">
                            <tr>
                                <th className="p-4">User Name</th>
                                <th className="p-4">Contact Info</th>
                                <th className="p-4">Role</th>
                                <th className="p-4">Assigned Company</th>
                                <th className="p-4 text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-800/60">
                            {users.map(u => (
                                <tr key={u.id} className="hover:bg-slate-900/50 transition-colors">
                                    <td className="p-4 font-bold text-white">
                                        <div className="flex items-center space-x-2">
                                            <div className="w-8 h-8 rounded-full bg-slate-800 flex items-center justify-center text-xs font-black text-indigo-300">
                                                {u.name.substring(0, 2).toUpperCase()}
                                            </div>
                                            <span>{u.name}</span>
                                        </div>
                                    </td>
                                    <td className="p-4 text-xs">
                                        <div className="text-slate-200">✉️ {u.email}</div>
                                        <div className="text-slate-400 mt-0.5">📱 {u.mobile_number}</div>
                                    </td>
                                    <td className="p-4">
                                        <span className={`px-2.5 py-1 rounded-full text-[11px] font-extrabold uppercase border ${
                                            u.role === 'admin' ? 'bg-purple-500/20 text-purple-300 border-purple-500/30' :
                                            u.role === 'coordinator' ? 'bg-cyan-500/20 text-cyan-300 border-cyan-500/30' :
                                            'bg-amber-500/20 text-amber-300 border-amber-500/30'
                                        }`}>
                                            {u.role}
                                        </span>
                                    </td>
                                    <td className="p-4 text-xs">
                                        {u.company ? (
                                            <span className="font-bold text-purple-300">🏢 {u.company.company_name}</span>
                                        ) : (
                                            <span className="text-slate-500">—</span>
                                        )}
                                    </td>
                                    <td className="p-4 text-right space-x-2">
                                        <button
                                            onClick={() => openEditModal(u)}
                                            className="p-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white transition-colors"
                                            title="Edit User"
                                        >
                                            <Edit3 className="w-4 h-4" />
                                        </button>

                                        {u.id !== currentUser?.id && (
                                            <button
                                                onClick={() => handleDelete(u.id)}
                                                className="p-2 rounded-lg bg-red-500/10 hover:bg-red-500/20 text-red-400 hover:text-red-300 transition-colors"
                                                title="Delete User"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Create/Edit Modal */}
            {showModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-md animate-fade-in">
                    <div className="w-full max-w-lg glass-card rounded-3xl p-6 sm:p-8 border border-slate-800 shadow-2xl relative">
                        <div className="flex items-center justify-between mb-4 border-b border-slate-800 pb-3">
                            <h2 className="text-xl font-bold text-white">
                                {editId ? 'Edit System User' : 'Create New System User'}
                            </h2>
                            <button
                                onClick={() => setShowModal(false)}
                                className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        {/* Top Modal Error Banner */}
                        {modalError && (
                            <div className="mb-4 p-3.5 rounded-xl bg-red-500/10 border border-red-500/30 text-red-400 text-xs font-semibold flex items-center space-x-2">
                                <AlertCircle className="w-4 h-4 text-red-400 shrink-0" />
                                <span>{modalError}</span>
                            </div>
                        )}

                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div>
                                <label className="block text-xs font-semibold text-slate-300 mb-1">Full Name *</label>
                                <input
                                    type="text"
                                    required
                                    value={formData.name}
                                    onChange={e => handleInputChange('name', e.target.value)}
                                    className={`w-full px-4 py-2.5 rounded-xl glass-input text-sm ${
                                        fieldErrors.name ? 'border-red-500 focus:border-red-500 ring-1 ring-red-500/30' : ''
                                    }`}
                                    placeholder="e.g. Adesh Jain"
                                />
                                {renderFieldError('name')}
                            </div>

                            <div>
                                <label className="block text-xs font-semibold text-slate-300 mb-1">Email Address *</label>
                                <input
                                    type="email"
                                    required
                                    value={formData.email}
                                    onChange={e => handleInputChange('email', e.target.value)}
                                    className={`w-full px-4 py-2.5 rounded-xl glass-input text-sm ${
                                        fieldErrors.email ? 'border-red-500 focus:border-red-500 ring-1 ring-red-500/30' : ''
                                    }`}
                                    placeholder="user@gmail.com"
                                />
                                {renderFieldError('email')}
                            </div>

                            <div>
                                <label className="block text-xs font-semibold text-slate-300 mb-1">Mobile Number (Unique) *</label>
                                <input
                                    type="tel"
                                    required
                                    value={formData.mobile_number}
                                    onChange={e => handleInputChange('mobile_number', e.target.value)}
                                    className={`w-full px-4 py-2.5 rounded-xl glass-input text-sm ${
                                        fieldErrors.mobile_number ? 'border-red-500 focus:border-red-500 ring-1 ring-red-500/30' : ''
                                    }`}
                                    placeholder="7000546005"
                                />
                                {renderFieldError('mobile_number')}
                            </div>

                            <div>
                                <label className="block text-xs font-semibold text-slate-300 mb-1">Role *</label>
                                <select
                                    value={formData.role}
                                    onChange={e => handleInputChange('role', e.target.value)}
                                    className={`w-full px-4 py-2.5 rounded-xl glass-input text-sm bg-slate-900 text-white ${
                                        fieldErrors.role ? 'border-red-500 focus:border-red-500 ring-1 ring-red-500/30' : ''
                                    }`}
                                >
                                    <option value="admin">Admin</option>
                                    <option value="coordinator">Coordinator</option>
                                    <option value="company_hr">Company HR</option>
                                </select>
                                {renderFieldError('role')}
                            </div>

                            {formData.role === 'company_hr' && (
                                <div>
                                    <label className="block text-xs font-semibold text-slate-300 mb-1">Assigned Company *</label>
                                    <select
                                        value={formData.company_id}
                                        onChange={e => handleInputChange('company_id', e.target.value)}
                                        className={`w-full px-4 py-2.5 rounded-xl glass-input text-sm bg-slate-900 text-white ${
                                            fieldErrors.company_id ? 'border-red-500 focus:border-red-500 ring-1 ring-red-500/30' : ''
                                        }`}
                                    >
                                        <option value="">Select a Company</option>
                                        {companies.map(c => (
                                            <option key={c.id} value={c.id}>{c.company_name}</option>
                                        ))}
                                    </select>
                                    {renderFieldError('company_id')}
                                </div>
                            )}

                            <div>
                                <label className="block text-xs font-semibold text-slate-300 mb-1">
                                    Password {editId ? '(Leave blank to keep unchanged)' : '(Optional, default 123456)'}
                                </label>
                                <div className="relative">
                                    <input
                                        type={showPassword ? 'text' : 'password'}
                                        value={formData.password}
                                        onChange={e => handleInputChange('password', e.target.value)}
                                        placeholder={editId ? '••••••••' : 'Default password set to 123456 if empty'}
                                        className={`w-full pl-4 pr-10 py-2.5 rounded-xl glass-input text-sm ${
                                            fieldErrors.password ? 'border-red-500 focus:border-red-500 ring-1 ring-red-500/30' : ''
                                        }`}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        className="absolute inset-y-0 right-0 pr-3.5 flex items-center text-slate-400 hover:text-slate-200 transition-colors"
                                        title={showPassword ? 'Hide password' : 'Show password'}
                                    >
                                        {showPassword ? <EyeOff className="w-4 h-4 text-cyan-400" /> : <Eye className="w-4 h-4" />}
                                    </button>
                                </div>
                                {renderFieldError('password')}
                            </div>

                            <div className="flex justify-end space-x-3 pt-4 border-t border-slate-800">
                                <button
                                    type="button"
                                    onClick={() => setShowModal(false)}
                                    className="px-4 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs font-semibold"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={submitting}
                                    className="px-5 py-2 rounded-xl bg-cyan-600 hover:bg-cyan-500 text-white text-xs font-bold shadow-lg shadow-cyan-600/30"
                                >
                                    {submitting ? 'Saving...' : (editId ? 'Update User' : 'Save User')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
